import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ClickAwayListener } from '@mui/material';
import {
  AddressSuggestionDropdown,
  useAddressAutocomplete,
  wrapperStyles,
} from 'app/shared/services/addressAutocomplete';
import PropTypes from 'prop-types';

import { transformNameInModelIntoFormName } from 'utils/formGenerator';

import { selectSortedCountiesByStates } from 'store/loanApplications/selectors';

import FormGeneratorTextFieldCDS from '../../formGenerator/FormGeneratorTextFieldCDS';

const AddressAutocompleteCDS = ({
  onChange,
  onSelect,
  dependentFields = [],
  setInForm,
  staticValidateField,
  name = '',
  isNestedForm = false,
  ...props
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const counties = useSelector(selectSortedCountiesByStates);
  const inputElRef = useRef(null);
  const { suggestions, search, clearSuggestions } = useAddressAutocomplete();

  const propsRef = useRef({
    counties,
    dependentFields,
    isNestedForm,
    name,
    setInForm,
    staticValidateField,
  });
  propsRef.current = {
    counties,
    dependentFields,
    isNestedForm,
    name,
    setInForm,
    staticValidateField,
  };

  const applyFormattedPlace = useCallback(formattedPlace => {
    if (!formattedPlace) {
      return;
    }

    const {
      counties: latestCounties,
      dependentFields: latestDependentFields,
      isNestedForm: latestIsNestedForm,
      name: latestName,
      setInForm: latestSetInForm,
      staticValidateField: latestStaticValidateField,
    } = propsRef.current;

    const transformedModelName = latestIsNestedForm
      ? latestName
      : transformNameInModelIntoFormName(latestName);
    const fullAddressModelName = `${latestName}.fullAddress`;
    const fullAddressTransformedModelName = latestIsNestedForm
      ? fullAddressModelName
      : transformNameInModelIntoFormName(`${latestName}.fullAddress`);
    const placeState = formattedPlace.state?.value;

    formattedPlace.county = latestCounties[placeState]?.find(
      ({ value }) => value?.toLowerCase() === formattedPlace.county?.toLowerCase(),
    );

    latestSetInForm(transformedModelName, formattedPlace.street);
    latestStaticValidateField?.(transformedModelName, formattedPlace.street);

    latestDependentFields.forEach(field => {
      const formName = latestIsNestedForm
        ? field.nameInModel
        : transformNameInModelIntoFormName(field.nameInModel);

      latestSetInForm(formName, formattedPlace[field.nameInSuggestion]);
      latestStaticValidateField?.(formName, formattedPlace[field.nameInSuggestion]);
    });

    latestSetInForm(
      fullAddressTransformedModelName,
      latestDependentFields.reduce(
        (acc, dependentField) => ({
          ...acc,
          [latestIsNestedForm
            ? dependentField.nameInModel
            : transformNameInModelIntoFormName(dependentField.nameInModel)]:
            formattedPlace[dependentField.nameInSuggestion],
        }),
        {},
      ),
    );
  }, []);

  const onReapiSelect = useCallback(
    suggestion => {
      if (onSelect) {
        onSelect(suggestion.mapped);
        clearSuggestions();

        return;
      }

      applyFormattedPlace({ ...suggestion.mapped });

      if (suggestion.mapped.reapiPropertyId) {
        const {
          name: latestName,
          isNestedForm: latestIsNestedForm,
          setInForm: latestSetInForm,
        } = propsRef.current;
        const addressPrefix = latestName.replace(/\.?street$/, '');
        const propertyPrefix = addressPrefix.replace(/\.?address$/, '');
        const reapiPath = propertyPrefix ? `${propertyPrefix}.reapiPropertyId` : 'reapiPropertyId';
        const formName = latestIsNestedForm
          ? reapiPath
          : transformNameInModelIntoFormName(reapiPath);
        latestSetInForm(formName, suggestion.mapped.reapiPropertyId);
      }

      clearSuggestions();
    },
    [applyFormattedPlace, clearSuggestions, onSelect],
  );

  const onInputChange = useCallback(
    e => {
      const { value } = e.target;
      setActiveIndex(-1);
      search(value);
      onChange?.(e);
    },
    [onChange, search],
  );

  const keyboardStateRef = useRef(null);
  keyboardStateRef.current = {
    suggestions,
    activeIndex,
    onReapiSelect,
    clearSuggestions,
  };

  useEffect(() => {
    const el = inputElRef.current;

    if (!el) {
      return;
    }

    const onKeyDown = e => {
      const state = keyboardStateRef.current;

      if (!state.suggestions.length) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setActiveIndex(prev => (prev < state.suggestions.length - 1 ? prev + 1 : 0));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setActiveIndex(prev => (prev > 0 ? prev - 1 : state.suggestions.length - 1));
      }

      if (e.key === 'Enter' && state.activeIndex >= 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        state.onReapiSelect(state.suggestions[state.activeIndex]);
      }

      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        state.clearSuggestions();
        setActiveIndex(-1);
      }
    };

    el.addEventListener('keydown', onKeyDown, true);

    return () => el.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const onItemMouseEnter = useCallback(e => {
    const index = Number(e.currentTarget.dataset.index);
    setActiveIndex(index);
  }, []);

  const onItemMouseLeave = useCallback(() => {
    setActiveIndex(-1);
  }, []);

  const onClickAway = useCallback(() => {
    clearSuggestions();
  }, [clearSuggestions]);

  const onItemMouseDown = useCallback(e => e.preventDefault(), []);

  const onItemClick = useCallback(
    e => {
      const index = Number(e.currentTarget.dataset.itemId);
      const suggestion = suggestions[index];

      if (suggestion) {
        onReapiSelect(suggestion);
      }
    },
    [suggestions, onReapiSelect],
  );

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div style={wrapperStyles}>
        <FormGeneratorTextFieldCDS
          name={name}
          variant="outlined"
          onChange={onInputChange}
          {...props}
          inputRef={inputElRef}
        />
        {!!suggestions.length && (
          <AddressSuggestionDropdown
            activeIndex={activeIndex}
            anchorEl={inputElRef.current}
            suggestions={suggestions}
            onItemClick={onItemClick}
            onItemMouseDown={onItemMouseDown}
            onItemMouseEnter={onItemMouseEnter}
            onItemMouseLeave={onItemMouseLeave}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};

export default AddressAutocompleteCDS;

AddressAutocompleteCDS.propTypes = {
  dependentFields: PropTypes.array,
  setInForm: PropTypes.func,
  staticValidateField: PropTypes.func,
  name: PropTypes.string,
  isNestedForm: PropTypes.bool,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
};
